import { useEffect, useState } from 'react';
import type { ExperienceListItem } from '../types';
import { BookingTypePanel } from '../components/BookingTypePanel';
import { useLightbox } from '../components/Lightbox';
import { trackEvent } from '../utils/track';
import { applyExperienceSeo } from '../utils/seo';
import { config } from '../config/env';

function buildWhatsAppLink(exp: ExperienceListItem): string {
  const slug = exp.slug || exp.experienceCode;
  return `${config.tekoblue.baseUrl}/${config.tekoblue.trackingSite}/${slug}`;
}

interface DetailProps {
  exp: ExperienceListItem;
}

function formatDuration(duration: string): string {
  if (!duration) return '';
  const parts = duration.split(':').map((n) => parseInt(n, 10));
  const h = parts[0] || 0;
  const m = parts[1] || 0;
  if (!h && !m) return duration;
  if (h && m) return `${h}h${m.toString().padStart(2, '0')}`;
  if (h) return `${h}h`;
  return `${m}min`;
}

function formatCapacity(min: number, max: number): string | null {
  if (!max && !min) return null;
  if (min && max && min !== max) return `${min}–${max} pessoas`;
  if (max) return `Até ${max} pessoas`;
  if (min) return `Mínimo ${min} pessoas`;
  return null;
}

function cleanPriceUnit(unit: string): string {
  return unit.replace(/^por\s+/i, '').trim();
}

// Normaliza chave de tag (sem acento, lowercase). Necessário porque o backend
// usa rótulos PT-BR ("Dificuldade", "Público") e queremos resiliência contra
// case / acentuação inconsistente.
function normalizeKey(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function pickTagByKey(tags: Record<string, string[]> | undefined, ...wanted: string[]): string[] {
  if (!tags) return [];
  const wantSet = new Set(wanted.map((w) => normalizeKey(w)));
  for (const [key, values] of Object.entries(tags)) {
    if (wantSet.has(normalizeKey(key))) {
      return values?.filter((t) => t && t.trim()) ?? [];
    }
  }
  return [];
}

// Tier classification — mesma lógica do ExperienceCard, mantém badge identico
// no hero do detalhe.
const tierClass = (tier: string): 'emalta' | 'exclusivo' | 'descubra' | '' => {
  const t = tier.toLowerCase().trim();
  if (!t) return '';
  if (t.includes('alta')) return 'emalta';
  if (t.includes('exclusiv')) return 'exclusivo';
  return 'descubra';
};

function TierIcon({ tier }: { tier: 'emalta' | 'exclusivo' | 'descubra' }) {
  if (tier === 'emalta') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
      </svg>
    );
  }
  if (tier === 'exclusivo') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
      </svg>
    );
  }
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}

// Icones SVG inline — mesmas formas usadas no ExperienceCard pra garantir
// continuidade visual entre home → detalhe.
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const RouteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="3 11 22 2 13 21 11 13 3 11" />
  </svg>
);
const UsersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const TagIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);
const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const MountainIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
  </svg>
);
const AlertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
// Quebra "restriction" em bullets quando o backend manda lista (separada por
// quebra de linha ou ponto-e-virgula). Texto unico vira lista de 1 item.
function splitRestrictions(text: string): string[] {
  if (!text) return [];
  const parts = text
    .split(/\r?\n|;/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  return parts.length > 0 ? parts : [text.trim()];
}

export function ExperienceDetailPage({ exp }: DetailProps) {
  const [slide, setSlide] = useState(0);
  const { open: openLightbox } = useLightbox();
  const cityName = exp.location.split(',').pop()?.trim();
  const tc = tierClass(exp.experienceTier);
  const isInstant = exp.bookingType === 'Confirmação Imediata';
  const pillLabel = isInstant ? 'Confirmação Imediata' : 'Resposta em até 24h';

  const handleWhatsAppClick = () =>
    trackEvent('whatsapp_clicked', {
      experienceCode: exp.experienceCode,
      experienceName: exp.title,
      bookingType: exp.bookingType,
      source: 'detail_cta',
    });

  // Canonical + JSON-LD Product em runtime — garante structured data correto
  // em TODA experiencia, inclusive as criadas apos o ultimo build (sem pre-render).
  useEffect(() => applyExperienceSeo(exp), [exp]);

  useEffect(() => {
    trackEvent('experience_detail_view', {
      experienceCode: exp.experienceCode,
      experienceName: exp.title,
      bookingType: exp.bookingType,
      city: exp.location,
      price: exp.price,
    });
  }, [exp.experienceCode, exp.title, exp.bookingType, exp.location, exp.price]);

  const handleShare = async () => {
    trackEvent('whatsapp_clicked', {
      experienceCode: exp.experienceCode,
      experienceName: exp.title,
      bookingType: exp.bookingType,
      source: 'detail_share',
    });
    const url = window.location.href;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: exp.title,
          text: exp.summary || exp.description || exp.title,
          url,
        });
      } catch {
        // user cancelled
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // ignore
      }
    }
  };

  const hasImages = exp.images && exp.images.length > 0;
  const multiple = hasImages && exp.images.length > 1;
  const next = () => setSlide((s) => (s + 1) % exp.images.length);
  const prev = () => setSlide((s) => (s - 1 + exp.images.length) % exp.images.length);

  const capacityLabel = formatCapacity(exp.minParticipants, exp.maxParticipants);
  const difficulty = pickTagByKey(exp.tags, 'dificuldade', 'difficulty')[0];
  const audienceTags = pickTagByKey(exp.tags, 'publico', 'audience');
  const restrictionLines = splitRestrictions(exp.restriction);
  const hasPrice = exp.price > 0;

  return (
    <main className="detail">
      <div className="detail-hero">
        {hasImages && (
          multiple ? (
            <div className="card-slider">
              {exp.images.map((img, i) => (
                <img
                  key={img}
                  className={`card-slider-img${i === slide ? ' active' : ''}`}
                  src={img}
                  alt={exp.title}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  onClick={() => openLightbox(exp.images, slide)}
                />
              ))}
              <button type="button" className="card-slider-prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Anterior">‹</button>
              <button type="button" className="card-slider-next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Próxima">›</button>
              <div className="card-slider-dots">
                {exp.images.map((_, i) => (
                  <span
                    key={i}
                    className={`card-slider-dot${i === slide ? ' active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSlide(i); }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <img
              className="detail-hero__img"
              src={exp.images[0]}
              alt={exp.title}
              onClick={() => openLightbox(exp.images, 0)}
            />
          )
        )}

        {/* Tier badge top-left + booking-type pill top-right: reusam EXATAMENTE
            as mesmas classes do ExperienceCard pra herdar a identidade visual. */}
        {exp.experienceTier && tc && (
          <div className="exp-tier-corner">
            <span className={`exp-tier-badge exp-tier-badge--${tc}`}>
              <TierIcon tier={tc} />
              {exp.experienceTier}
            </span>
          </div>
        )}

        <div className="exp-booking-corner">
          <span className={`booking-type-badge booking-type-badge--overlay booking-type-badge--${isInstant ? 'instant' : 'request'}`}>
            <span className="booking-type-badge__dot" aria-hidden="true" />
            <span className="booking-type-badge__label">{pillLabel}</span>
          </span>
        </div>

        <a href="/" className="detail-hero__back" aria-label="Voltar">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </a>

        <button type="button" className="detail-hero__share" onClick={handleShare} aria-label="Compartilhar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
        </button>
      </div>

      <div className="detail__body">
        {/* Cabeçalho: cidade-pill (verde) → título display → resumo curto.
            Mesma cascata visual do .exp-card .card-body, só com fonts maiores. */}
        <header className="detail__header">
          {cityName && (
            <span className="exp-city-label detail__city">
              <PinIcon />
              {cityName}
            </span>
          )}
          <h1 className="detail__title">{exp.title}</h1>

 {/* Quick-info chips: usa exp-quick-info do card pra manter espacamento
            e tipografia identicos. Dificuldade entra aqui (não nas tags). */}
        {(exp.duration || exp.distanceKm || capacityLabel || difficulty || exp.category) && (
          <div className="exp-quick-info detail__quick-info">
            {exp.duration && (
              <span className="exp-quick-item">
                <ClockIcon />
                {formatDuration(exp.duration)}
              </span>
            )}
            {exp.distanceKm != null && exp.distanceKm > 0 && (
              <span className="exp-quick-item">
                <RouteIcon />
                {exp.distanceKm.toFixed(1)} km
              </span>
            )}
            {capacityLabel && (
              <span className="exp-quick-item">
                <UsersIcon />
                {capacityLabel}
              </span>
            )}
            {difficulty && (
              <span className="exp-quick-item">
                <MountainIcon />
                {difficulty}
              </span>
            )}
            {exp.category && (
              <span className="exp-quick-item">
                <TagIcon />
                {exp.category}
              </span>
            )}
          </div>
        )}

          {exp.summary && <p className="detail__summary">{exp.summary}</p>}
        </header>

        {exp.description && (
          <section className="detail__section">
            <h2 className="detail__section-title">Sobre a <span className="title-serif">Experiência</span></h2>
            <p className="detail__section-body">{exp.description}</p>
          </section>
        )}

        <section className="detail__section">
          <h2 className="detail__section-title">Como Funciona?</h2>
          <BookingTypePanel type={exp.bookingType} />
        </section>

        {audienceTags.length > 0 && (
          <section className="detail__section">
            <h2 className="detail__section-title">Indicado para</h2>
            <div className="detail__tag-chips">
              {audienceTags.map((tag) => (
                <span key={tag} className="detail__tag-chip">{tag}</span>
              ))}
            </div>
          </section>
        )}

        {restrictionLines.length > 0 && (
          <section className="detail__section">
            <h2 className="detail__section-title">Antes de Reservar</h2>
            <div className="detail__notice" role="note">
              <span className="detail__notice-icon" aria-hidden="true">
                <AlertIcon />
              </span>
              <div className="detail__notice-body">
                <strong className="detail__notice-title">Pontos de atenção</strong>
                {restrictionLines.length === 1 ? (
                  <p className="detail__notice-text">{restrictionLines[0]}</p>
                ) : (
                  <ul className="detail__notice-list">
                    {restrictionLines.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
        )}
      </div>

      <footer className="detail__cta-bar">
        {hasPrice && (
          <div className="detail__cta-price">
            <span className="exp-price-label">A partir de</span>
            <span className="exp-price-value">
              <span className="exp-price-currency">{exp.currency}</span>
              <strong>{Math.floor(exp.price)}</strong>
            </span>
            {exp.priceUnit && (
              <span className="exp-price-unit">por {cleanPriceUnit(exp.priceUnit)}</span>
            )}
          </div>
        )}
        {/* WhatsApp = secundário (outline verde). Reservar = primário (foco). */}
        <div className="detail__cta-actions">
          <a
            href={buildWhatsAppLink(exp)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--lg detail__cta-talk"
            onClick={handleWhatsAppClick}
            aria-label={`Falar no WhatsApp sobre ${exp.title}`}
          >
            <span className="detail__cta-talk-label">WhatsApp</span>
          </a>
          <a
            href={`/booking/${encodeURIComponent(exp.experienceCode)}`}
            className="btn btn--brand btn--lg detail__cta-book"
          >
            Reservar
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </footer>
    </main>
  );
}
